import React, { Fragment, type PropsWithChildren, useContext, useState } from "react";
import { Fab } from "@mui/material";
import {
  FiArchive,
  FiClipboard,
  FiFrown,
  FiKey,
  FiLogIn,
  FiLogOut,
  FiPrinter,
  FiShare,
  FiTrello,
} from "react-icons/fi";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";

import { api, Booking, BookingStatus, VehicleStatus } from "../../../logic/api";
import styles from "./styles";
import AnimateFrom from "../../../components/animations/AnimateFrom";
import { useLabelPrinter } from "../../../components/labels";
import { useLabelTemplates } from "../../../components/labels/labelTemplates";
import { SessionContext } from "../../../utility/useSession";
import { isAdmin } from "../../../utility/utils";
import { BookingDetailsPrint } from "../BookingDetailPrint/BookingDetailsPrint";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";

export const BookingFabLabel = (props: PropsWithChildren<{
  text: string;
}>) => {
  return (
    <Fragment>
      <div style={styles.fabLabel}>{props.text}</div>
      {props.children}
    </Fragment>
  );
};

export class BookingFabController {
  printCheckInBarcodes = (booking: Booking) => { console.log("No handler assigned for printCheckInBarcodes", booking); }
}

const BookingFab = (props: {
  allowUpdatingBooking: boolean;
  booking: Booking;
  controller: BookingFabController;
  onRequestLabelPrint: (booking: Booking) => Promise<void>;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onCancel: () => void;
  onUnArchive: () => void;
  onShowComplaints: () => void;
  onShowInspections: () => void;
  onShowDataRetention: () => void;
  onShowAllowKeyRelease: () => void;
  onUndoCheckOut?: (booking: Booking) => void;
  onUndoCheckIn?: (booking: Booking) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const deprecateArchiveReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateArchiveStatus);
  const bookingUndoCheckout = useReleaseManagement(ReleaseFlag.releaseBookingUndoCheckout);
  const isDataManagementReleased = useReleaseManagement(ReleaseFlag.releaseDataManagement);
  const isUndoCheckInReleased = useReleaseManagement(ReleaseFlag.releaseUndoCheckIn);

  const buttons: JSX.Element[] = [];
  const { labelPrinter } = useLabelPrinter();
  const {
    generateLockerQRPrintJob,
    generateParkAndRidePrintJob,
    generateMeetAndGreetPrintJob,
    generateVehiclePrintJob
  } = useLabelTemplates();

  const reprintKioskQR = api.hooks.printerLabel.rePrintKioskQRCode();

  const createMenuItem = (
    text: string,
    icon: JSX.Element,
    id: string,
    color?: string,
    onClick?: () => void
  ) => {
    buttons.push(
      <Fab
        style={
          color == null ? styles.fab : { ...styles.fab, backgroundColor: color }
        }
        onClick={() => {
          setExpanded(false);
          if (onClick) {
            onClick();
          }
        }}
        data-testid={id}
      >
        <BookingFabLabel text={text}>{icon}</BookingFabLabel>
      </Fab>
    );
  };


  let booking_checkout_admin_enabled = false;
  if (isAdmin(session)) {
    if (session.current.site != null) {
      booking_checkout_admin_enabled = session.current.site.preferences.booking_checkout_admin_enabled;
    }
  }

  let complaints_enabled = false;
  let inspections_enabled = false;
  let keyLockerAllowKey_enabled = false;

  if (session.current.site != null) {
    complaints_enabled = session.current.site.preferences.complaints_enabled == true;
    inspections_enabled = session.current.site.preferences.inspections_enabled == true;
    if (props.booking.vehicle.statusId == VehicleStatus.CheckedIn) {

      keyLockerAllowKey_enabled = session.current.site.preferences.keyLockerAllowKey_enabled == true;


    }
  }



  buttons.push(<BookingDetailsPrint booking={props.booking} onClick={() => setExpanded(false)} />);

  if (props.booking.statusId == BookingStatus.Cancelled && !deprecateArchiveReleased) {
    createMenuItem(
      t("booking.labels.unarchive"),
      <FiShare size={26}></FiShare>,
      'Fab-Unarchive',
      "rgb(246, 166, 86)",
      props.onUnArchive
    );
  }

  if (props.booking.vehicle.statusId == VehicleStatus.NotCheckedIn || props.booking.vehicle.statusId == VehicleStatus.PreCheckIn) {
    if (session.current.site != null) {
      if (session.current.site.preferences.allow_printing_labels_before_checkin == true) {
        createMenuItem(
          t("booking.labels.printLabel"),
          <FiPrinter size={26}></FiPrinter>,
          "Fab-Print-Label",
          "rgb(13, 89, 87)",
          () => {
            props.onRequestLabelPrint(props.booking);
          }
        );
      }

      if (session.current.site.preferences.booking_checkout_without_checkin_enabled == true && booking_checkout_admin_enabled == true && hasPermission("bookingManagement.booking.updateStatus")) {
        createMenuItem(
          t("booking.labels.checkout"),
          <FiLogOut size={26}></FiLogOut>,
          "Fab-Check-Out",
          "rgb(246, 166, 86)",
          props.onCheckOut
        );
      }
    }



    if (props.booking.statusId != BookingStatus.Cancelled) {
      if (props.allowUpdatingBooking == true) {
        if (hasPermission("bookingManagement.booking.cancel")) {
          createMenuItem(
            deprecateArchiveReleased ? t("booking.labels.cancelBooking") : t("booking.labels.archiveBooking"),
            <FiArchive size={26}></FiArchive>,
            deprecateArchiveReleased ? "Fab-Cancel-Booking" : "Fab-Archive-Booking",
            "rgb(246, 166, 86)",
            props.onCancel
          );
        }
        if (hasPermission("bookingManagement.booking.updateStatus")) {
          createMenuItem(
            t("booking.labels.checkin"),
            <FiLogIn size={26}></FiLogIn>,
            "Fab-Check-In",
            "rgb(142, 189, 76)",
            props.onCheckIn
          );
        }
      }
    }

    //    createMenuItem("Sales Pair Reciept", <FiPaperclip size={26}></FiPaperclip>);
  } else if (props.booking.vehicle.statusId == VehicleStatus.CheckedIn) {
    if (keyLockerAllowKey_enabled) {
      createMenuItem(t("booking.labels.allowKeyRelease"), <FiKey size={26}></FiKey>, "Fab-Allow-Key-Release", "rgb(58, 183, 169)", () => {
        props.onShowAllowKeyRelease();
      });
    }

    if (session.current.site != null) {
      if (session.current.site.preferences.booking_locker_reprint_enabled == true) {
        createMenuItem(
          t("booking.labels.printLockerQR"),
          <FiPrinter size={26}></FiPrinter>,
          "Fab-Print-Locker-QR-Code",
          "rgb(13, 89, 87)",
          () => {
            reprintKioskQR.send(session.current.context, {
              uuid: props.booking.uuid,
            }, (data) => {
              if (data != null) {
                labelPrinter.print(generateLockerQRPrintJob(props.booking, data));
              }
            });
          }
        );
      }
    }

    createMenuItem(
      t("booking.labels.printLabel"),
      <FiPrinter size={26}></FiPrinter>,
      "Fab-Print-Label",
      "rgb(13, 89, 87)",
      () => {
        props.onRequestLabelPrint(props.booking);
      }
    );

    if ((props.allowUpdatingBooking == true || booking_checkout_admin_enabled == true) && hasPermission("bookingManagement.booking.updateStatus")) {

      /*createMenuItem(
        "Print Vehicle Tag",
        <FiPrinter size={26}></FiPrinter>,
        "rgb(13, 89, 87)",
        () => {
          labelPrinter.print(generateVehiclePrintJob(props.booking));
        }
      );*/

      createMenuItem(
        t("booking.labels.checkout"),
        <FiLogOut size={26}></FiLogOut>,
        "Fab-Check-Out",
        "rgb(246, 166, 86)",
        props.onCheckOut
      );
    }
  } else if (props.booking.vehicle.statusId == VehicleStatus.CheckedOut) {
    if (props.allowUpdatingBooking == true) {
      createMenuItem(
        t("booking.labels.printLabel"),
        <FiPrinter size={26}></FiPrinter>,
        "Fab-Print-Label",
        "rgb(13, 89, 87)",
        () => {
          props.onRequestLabelPrint(props.booking);
        }
      );

      if (props.booking.statusId != BookingStatus.Cancelled && !deprecateArchiveReleased && hasPermission("bookingManagement.booking.cancel")) {
        createMenuItem(
          t("booking.labels.archiveBooking"),
          <FiArchive size={26}></FiArchive>,
          "Fab-Archive-Booking",
          "rgb(246, 166, 86)",
          props.onCancel
        );
      }
    }

    if (bookingUndoCheckout && hasPermission("bookingManagement.booking.revert")) {
      createMenuItem(
        t("booking.labels.undoCheckout"),
        <UndoIcon style={{ "fontSize": 26 }}></UndoIcon>,
        "Fab-Undo-Checkout",
        "rgb(180, 117, 67)",
        () => {
          props.onUndoCheckOut && props.onUndoCheckOut(props.booking);
        }
      );
    }
  }

  if (complaints_enabled) {
    createMenuItem(t("complaints.title"), <FiFrown size={26}></FiFrown>, "Fab-Complaints", "rgb(58, 183, 169)", () => {
      props.onShowComplaints();
    });
  }

  if (inspections_enabled) {
    createMenuItem(t("booking.labels.inspections"), <FiClipboard size={26}></FiClipboard>, "Fab-Inspections", "rgb(58, 183, 169)", () => {
      props.onShowInspections();
    });
  }

  if (isAdmin(session) && isDataManagementReleased && hasPermission("bookingManagement.data.retain")) {
    createMenuItem(t("booking.dataRetention.menuOption"), <FiTrello size={26}></FiTrello>, "Fab-Retention", "rgb(100, 100, 100)", () => {
      props.onShowDataRetention();
    });
  }

  if (isUndoCheckInReleased && session.current.site?.preferences.undo_check_in === true && props.booking.vehicle.statusId === VehicleStatus.CheckedIn && hasPermission("bookingManagement.booking.revert")) {
    createMenuItem(
      t("booking.labels.undoCheckin"),
      <UndoIcon style={{ fontSize: 26 }} />,
      "Fab-Undo-Checkin",
      "rgb(180, 117, 67)",
      () => {
        if (props.onUndoCheckIn) {
          props.onUndoCheckIn(props.booking);
        }
      }
    );
  }

  props.controller.printCheckInBarcodes = async (booking: Booking) => {


    if (booking.selfPark == true) {
      await new Promise<void>((accept) => {
        labelPrinter.print(generateParkAndRidePrintJob(booking), accept);
      });
    } else if (booking.collection == true) {
      await new Promise<void>((accept) => {
        labelPrinter.print(generateMeetAndGreetPrintJob(booking), accept);
      });
    } else if (booking.delivery == true) {
      await new Promise<void>((accept) => {
        labelPrinter.print(generateMeetAndGreetPrintJob(booking), accept);
      });
    } else {
      await new Promise<void>((accept) => {
        labelPrinter.print(generateVehiclePrintJob(booking), accept);
      });
    }

  };

  if (buttons.length == 0) {
    return <Fragment />
  }

  return (
    <Fragment>
      {expanded == true && (
        <AnimateFrom
          delay={0}
          duration={1}
          style={{
            opacity: 0,
          }}
        >
          {" "}
          <div style={styles.fabBg}></div>
        </AnimateFrom>
      )}

      <div style={styles.fabPosition}>
        {expanded == true && (
          <div>
            {React.Children.map(buttons, (button, i) => {
              return (
                <div key={i}>
                  <AnimateFrom
                    delay={0.1 * (buttons.length - i)}
                    style={{
                      opacity: 0,
                      zoom: 2,
                    }}
                  >
                    {" "}
                    {button}{" "}
                  </AnimateFrom>
                </div>
              );
            })}
          </div>
        )}
        <Fab
          style={
            expanded == true
              ? { ...styles.fab, ...styles.fabClose, ...styles.fabRoot }
              : { ...styles.fab, ...styles.fabRoot }
          }
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded == false && <MenuIcon style={{ fontSize: 26 }}></MenuIcon>}
          {expanded == true && <CloseIcon style={{ fontSize: 26 }}></CloseIcon>}
        </Fab>
      </div>
    </Fragment>
  );
};

export default BookingFab;
